// frontend/src/utils/dateUtils.ts
/**
 * Date/Time utility functions for timezone handling
 * All dates in the backend are stored in UTC
 * All dates displayed to users are ALWAYS in Paris timezone (Europe/Paris)
 */

/**
 * Convert a datetime-local input string to UTC ISO string
 * Assumes the input is in Paris timezone (since organizers are in Paris)
 * Input: "2025-01-15T18:30" (Paris time)
 * Output: "2025-01-15T17:30:00.000Z" (UTC, if Paris is UTC+1)
 */
export const localDateTimeToUTC = (localDateTime: string): string => {
  if (!localDateTime) return '';
  
  // Create a date object from the local datetime string
  // The Date constructor interprets this as local time
  const localDate = new Date(localDateTime);
  
  // Return ISO string (which is in UTC)
  return localDate.toISOString();
};

/**
 * Convert UTC ISO string to datetime-local input format (for form inputs)
 * Converts to Paris timezone for the datetime-local input
 * Input: "2025-01-15T17:30:00.000Z" (UTC)
 * Output: "2025-01-15T18:30" (Paris time for datetime-local input)
 */
export const utcToLocalDateTime = (utcString: string): string => {
  if (!utcString) return '';
  
  const date = new Date(utcString);
  
  // Format for datetime-local input: YYYY-MM-DDTHH:mm
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Format UTC date for display to users (ALWAYS in Paris timezone)
 * Input: "2025-01-15T17:30:00.000Z" (UTC)
 * Output: "mercredi 15 janvier 2025 à 18:30 (heure de Paris)" 
 * 
 * Note: This will show "18:30 (heure de Paris)" to EVERYONE, 
 * regardless of their browser timezone
 */
export const formatDateForDisplay = (
  utcString: string, 
  options?: Intl.DateTimeFormatOptions,
  showTimezone: boolean = true
): string => {
  if (!utcString) return '';
  
  const date = new Date(utcString);
  
  const defaultOptions: Intl.DateTimeFormatOptions = {
    timeZone: 'Europe/Paris', // ✅ Force Paris timezone
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  };
  
  const formatted = date.toLocaleDateString('fr-FR', defaultOptions);
  
  // Add timezone indicator if requested
  if (showTimezone) {
    return `${formatted} (heure de Paris)`;
  }
  
  return formatted;
};

/**
 * Format UTC date for short display (ALWAYS in Paris timezone)
 * Input: "2025-01-15T17:30:00.000Z" (UTC)
 * Output: "15/01/2025 18:30"
 */
export const formatDateShort = (utcString: string): string => {
  if (!utcString) return '';
  
  const date = new Date(utcString);
  
  return date.toLocaleDateString('fr-FR', {
    timeZone: 'Europe/Paris', // ✅ Force Paris timezone
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Format date for admin displays (without timezone suffix for cleaner UI)
 * Input: "2025-01-15T17:30:00.000Z" (UTC)
 * Output: "15 janvier 2025, 18:30"
 */
export const formatDateAdmin = (utcString: string): string => {
  if (!utcString) return '';
  
  const date = new Date(utcString);
  
  return date.toLocaleDateString('fr-FR', {
    timeZone: 'Europe/Paris',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Get timezone offset string for Paris at a given date
 * Returns: "CET" (winter) or "CEST" (summer)
 */
export const getParisTimezoneAbbr = (date: Date = new Date()): string => {
  const formatter = new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    timeZoneName: 'short',
  });
  
  const parts = formatter.formatToParts(date);
  const timeZonePart = parts.find(part => part.type === 'timeZoneName');
  
  return timeZonePart?.value || 'CET';
};